// packages/risp-admin/src/context/breadcrumbs.tsx
import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';

export type BreadcrumbItem = {
  label: ReactNode;
  /** Either `path` or `href` may be supplied; the breadcrumb is rendered as a link when set. */
  path?: string;
  href?: string;
};

type BreadcrumbsContextValue = {
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (items: BreadcrumbItem[]) => void;
};

const BreadcrumbsContext = createContext<BreadcrumbsContextValue>({
  breadcrumbs: [],
  setBreadcrumbs: () => {},
});

export const BreadcrumbsProvider = ({ children }: { children: ReactNode }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const set = useCallback((items: BreadcrumbItem[]) => setBreadcrumbs(items), []);
  return (
    <BreadcrumbsContext.Provider value={{ breadcrumbs, setBreadcrumbs: set }}>{children}</BreadcrumbsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useBreadcrumbs = () => useContext(BreadcrumbsContext).breadcrumbs;

// eslint-disable-next-line react-refresh/only-export-components
export const useSetBreadcrumbs = (items: BreadcrumbItem[]) => {
  const { setBreadcrumbs } = useContext(BreadcrumbsContext);
  const pathKey = items.map(i => `${typeof i.label === 'string' ? i.label : ''}|${i.path ?? i.href ?? ''}`).join('::');

  useEffect(() => {
    setBreadcrumbs(items);
    return () => setBreadcrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathKey, setBreadcrumbs]);
};
